import { Looker40SDK } from "@looker/sdk";
import { find, unionWith } from "lodash";
import {
  NonCompliantReason,
  NonCompliantRecord,
  ComplianceType,
  NonComplianceMap,
  ParticipantComplianceRecord,
  ParticipantRecord,
  participantsSyncComplianceCache,
  participantsWearComplianceCache,
  participantsMedLogDailyComplianceCache,
  participantsMedLogWeeklyComplianceCache,
  participantsECGReadingDailyComplianceCache,
} from "./QueryResultsCache";
import { LookerDashboardId } from "generated/studydata/studydata_pb";
import {
  lookerReturnsInvalidResults,
  telemetry,
} from "core/TelemetryController";

/**
 * Queries Looker to get participant wear compliance records for a week and site.
 *
 * The function assumes that the Looker user attributes for week and site are
 * set up properly.
 *
 * See instructions below about how to get the Looker query id and query field names
 * used in this function:
 *   go/verily-devices-looker-dashboards
 */
async function getWearComplianceRecords(
  sdk: Looker40SDK,
  previousWeekOffset: number,
  lookerQueryIdsMap: Map<LookerDashboardId, string>
) {
  let records = new Array<ParticipantComplianceRecord>();
  let hasError = false;

  try {
    if (participantsWearComplianceCache.has(previousWeekOffset)) {
      return {
        complianceType: ComplianceType.Wear,
        records: participantsWearComplianceCache.get(previousWeekOffset)!,
        hasError: false,
      };
    }

    const results = await sdk.ok(
      sdk.run_query({
        query_id: lookerQueryIdsMap.get(
          LookerDashboardId.PARTICIPANTS_WEAR_COMPLIANCE
        )!,
        result_format: "json",
      })
    );

    if (results && Array.isArray(results)) {
      for (let row of results) {
        records.push({
          participantId: row["participants_wear_compliance.participant_id"],
          subjectId: row["participants_wear_compliance.subject_id"],
          deviceId: row["participants_wear_compliance.device_id"],
          siteId: "",
          isCompliant: row["participants_wear_compliance.is_compliant"] === 1,
        });
      }

      // Store the results in cache.
      participantsWearComplianceCache.set(previousWeekOffset, records);
    } else {
      telemetry.logError(new Error(lookerReturnsInvalidResults));
      hasError = true;
    }
  } catch (error) {
    telemetry.logError(error);
    hasError = true;
  }

  return {
    complianceType: ComplianceType.Wear,
    records: records,
    hasError: hasError,
  };
}

/**
 * Queries Looker to get participants sync compliance records for a week and site.
 *
 * The function assumes that the Looker user attributes for week and site are
 * set up properly.
 *
 * See instructions below about how to get the Looker query id and query field names
 * used in this function:
 *   go/verily-devices-looker-dashboards
 */
async function getSyncComplianceRecords(
  sdk: Looker40SDK,
  previousWeekOffset: number,
  lookerQueryIdsMap: Map<LookerDashboardId, string>
) {
  let records = new Array<ParticipantComplianceRecord>();
  let hasError = false;

  try {
    if (participantsSyncComplianceCache.has(previousWeekOffset)) {
      return {
        complianceType: ComplianceType.Sync,
        records: participantsSyncComplianceCache.get(previousWeekOffset)!,
        hasError: false,
      };
    }

    const results = await sdk.ok(
      sdk.run_query({
        query_id: lookerQueryIdsMap.get(
          LookerDashboardId.PARTICIPANTS_SYNC_COMPLIANCE
        )!,
        result_format: "json",
      })
    );

    if (results && Array.isArray(results)) {
      for (let row of results) {
        records.push({
          participantId: row["participants_sync_compliance.participant_id"],
          subjectId: row["participants_sync_compliance.subject_id"],
          deviceId: row["participants_sync_compliance.device_id"],
          siteId: "",
          isCompliant: row["participants_sync_compliance.is_compliant"] === 1,
        });
      }

      // Store the results in cache.
      participantsSyncComplianceCache.set(previousWeekOffset, records);
    } else {
      telemetry.logError(new Error(lookerReturnsInvalidResults));
      hasError = true;
    }
  } catch (error) {
    telemetry.logError(error);
    hasError = true;
  }

  return {
    complianceType: ComplianceType.Sync,
    records: records,
    hasError: hasError,
  };
}

/**
 * Queries Looker to get participant medication logging daily compliance records for a week and site.
 *
 * The function assumes that the Looker user attributes for week and site are
 * set up properly.
 *
 * See instructions below about how to get the Looker query id and query field names
 * used in this function:
 *   go/verily-devices-looker-dashboards
 */
async function getMedLogDailyComplianceRecords(
  sdk: Looker40SDK,
  previousWeekOffset: number,
  lookerQueryIdsMap: Map<LookerDashboardId, string>
) {
  let records = new Array<ParticipantComplianceRecord>();
  let hasError = false;

  try {
    if (participantsMedLogDailyComplianceCache.has(previousWeekOffset)) {
      return {
        complianceType: ComplianceType.MedLogDaily,
        records:
          participantsMedLogDailyComplianceCache.get(previousWeekOffset)!,
        hasError: false,
      };
    }

    const results = await sdk.ok(
      sdk.run_query({
        query_id: lookerQueryIdsMap.get(
          LookerDashboardId.PARTICIPANTS_MED_LOGGING_DAILY_COMPLIANCE
        )!,
        result_format: "json",
      })
    );

    if (results && Array.isArray(results)) {
      for (let row of results) {
        records.push({
          participantId:
            row["participants_med_logging_daily_compliance.participant_id"],
          subjectId:
            row["participants_med_logging_daily_compliance.subject_id"],
          deviceId: row["participants_med_logging_daily_compliance.device_id"],
          siteId: "",
          isCompliant:
            row["participants_med_logging_daily_compliance.is_compliant"] === 1,
        });
      }

      // Store the results in cache.
      participantsMedLogDailyComplianceCache.set(previousWeekOffset, records);
    } else {
      telemetry.logError(new Error(lookerReturnsInvalidResults));
      hasError = true;
    }
  } catch (error) {
    telemetry.logError(error);
    hasError = true;
  }

  return {
    complianceType: ComplianceType.MedLogDaily,
    records: records,
    hasError: hasError,
  };
}

/**
 * Queries Looker to get participant ECG reading daily compliance records for a week and site.
 *
 * The function assumes that the Looker user attributes for week and site are
 * set up properly.
 *
 * See instructions below about how to get the Looker query id and query field names
 * used in this function:
 *   go/verily-devices-looker-dashboards
 */
async function getECGReadingDailyComplianceRecords(
  sdk: Looker40SDK,
  previousWeekOffset: number,
  lookerQueryIdsMap: Map<LookerDashboardId, string>
) {
  let records = new Array<ParticipantComplianceRecord>();
  let hasError = false;

  try {
    if (participantsECGReadingDailyComplianceCache.has(previousWeekOffset)) {
      return {
        complianceType: ComplianceType.ECGReadingDaily,
        records:
          participantsECGReadingDailyComplianceCache.get(previousWeekOffset)!,
        hasError: false,
      };
    }

    const results = await sdk.ok(
      sdk.run_query({
        query_id: lookerQueryIdsMap.get(
          LookerDashboardId.PARTICIPANTS_ECG_READING_DAILY_COMPLIANCE
        )!,
        result_format: "json",
      })
    );

    if (results && Array.isArray(results)) {
      for (let row of results) {
        records.push({
          participantId:
            row["participants_ecg_reading_daily_compliance.participant_id"],
          subjectId:
            row["participants_ecg_reading_daily_compliance.subject_id"],
          deviceId: row["participants_ecg_reading_daily_compliance.device_id"],
          siteId: "",
          isCompliant:
            row["participants_ecg_reading_daily_compliance.is_compliant"] === 1,
        });
      }

      // Store the results in cache.
      participantsECGReadingDailyComplianceCache.set(
        previousWeekOffset,
        records
      );
    } else {
      telemetry.logError(new Error(lookerReturnsInvalidResults));
      hasError = true;
    }
  } catch (error) {
    telemetry.logError(error);
    hasError = true;
  }

  return {
    complianceType: ComplianceType.ECGReadingDaily,
    records: records,
    hasError: hasError,
  };
}

/**
 * Queries Looker to get participant medication logging weekly compliance records for a week and site.
 *
 * The function assumes that the Looker user attributes for week and site are
 * set up properly.
 *
 * See instructions below about how to get the Looker query id and query field names
 * used in this function:
 *   go/verily-devices-looker-dashboards
 */
async function getMedLogWeeklyComplianceRecords(
  sdk: Looker40SDK,
  previousWeekOffset: number,
  lookerQueryIdsMap: Map<LookerDashboardId, string>
) {
  let records = new Array<ParticipantComplianceRecord>();
  let hasError = false;

  try {
    if (participantsMedLogWeeklyComplianceCache.has(previousWeekOffset)) {
      return {
        complianceType: ComplianceType.MedLogWeekly,
        records:
          participantsMedLogWeeklyComplianceCache.get(previousWeekOffset)!,
        hasError: false,
      };
    }

    const results = await sdk.ok(
      sdk.run_query({
        query_id: lookerQueryIdsMap.get(
          LookerDashboardId.PARTICIPANTS_MED_LOGGING_WEEKLY_COMPLIANCE
        )!,
        result_format: "json",
      })
    );

    if (results && Array.isArray(results)) {
      for (let row of results) {
        records.push({
          participantId:
            row["participants_med_logging_weekly_compliance.participant_id"],
          subjectId:
            row["participants_med_logging_weekly_compliance.subject_id"],
          deviceId: row["participants_med_logging_weekly_compliance.device_id"],
          siteId: "",
          isCompliant:
            row["participants_med_logging_weekly_compliance.is_compliant"] ===
            1,
        });
      }

      // Store the results in cache.
      participantsMedLogWeeklyComplianceCache.set(previousWeekOffset, records);
    } else {
      telemetry.logError(new Error(lookerReturnsInvalidResults));
      hasError = true;
    }
  } catch (error) {
    telemetry.logError(error);
    hasError = true;
  }

  return {
    complianceType: ComplianceType.MedLogWeekly,
    records: records,
    hasError: hasError,
  };
}

/**
 * Queries Looker to get participants compliance records.
 *
 * The function assumes that the Looker user attributes for week and site are
 * set up properly.
 */
export async function getParticipantsComplianceRecords(
  sdk: Looker40SDK,
  previousWeekOffset: number,
  optionalComplianceTypes: ComplianceType[] = [],
  lookerQueryIdsMap: Map<LookerDashboardId, string>
) {
  let activeParticipants = 0;
  const compliantRecords = new Array<ParticipantRecord>();
  const noncompliantRecords = new Array<NonCompliantRecord>();

  try {
    // Get array of required compliance records
    const getComplianceRecords = [
      getWearComplianceRecords(sdk, previousWeekOffset, lookerQueryIdsMap),
      getSyncComplianceRecords(sdk, previousWeekOffset, lookerQueryIdsMap),
    ];

    // Get compliance records for optional compliance metrics
    for (let opt of optionalComplianceTypes) {
      switch (opt) {
        case ComplianceType.MedLogDaily:
          getComplianceRecords.push(
            getMedLogDailyComplianceRecords(
              sdk,
              previousWeekOffset,
              lookerQueryIdsMap
            )
          );
          break;
        case ComplianceType.MedLogWeekly:
          getComplianceRecords.push(
            getMedLogWeeklyComplianceRecords(
              sdk,
              previousWeekOffset,
              lookerQueryIdsMap
            )
          );
          break;
        case ComplianceType.ECGReadingDaily:
          getComplianceRecords.push(
            getECGReadingDailyComplianceRecords(
              sdk,
              previousWeekOffset,
              lookerQueryIdsMap
            )
          );
          break;
      }
    }

    // Start async functions simultaneously
    const results = await Promise.all(getComplianceRecords);
    const hasError = results.some((result) => {
      return result.hasError;
    });
    if (hasError) {
      return {
        activeParticipantsCount: 0,
        compliantRecords: new Array<ParticipantRecord>(),
        noncompliantRecords: new Array<NonCompliantRecord>(),
        hasError: true,
      };
    }

    const keys = results
      .map((result) => result.records)
      .reduce((acc, records) => {
        return unionWith(acc, records, (obj1, obj2) => {
          return (
            obj1.participantId === obj2.participantId &&
            obj1.subjectId === obj2.subjectId &&
            obj1.deviceId === obj2.deviceId
          );
        });
      });

    activeParticipants = keys.length;

    for (let key of keys) {
      const lookupKey = {
        participantId: key.participantId,
        subjectId: key.subjectId,
        deviceId: key.deviceId,
      };

      const reasons = new Set<NonCompliantReason>();

      results.forEach((result) => {
        let records = result.records;
        let record = find<ParticipantComplianceRecord>(records, lookupKey);

        if (record === undefined || record.isCompliant === false) {
          reasons.add(NonComplianceMap.get(result.complianceType)!);
        }
      });

      if (reasons.size > 0) {
        noncompliantRecords.push({
          ...key,
          siteId: "",
          reasons: reasons,
        });
      } else {
        compliantRecords.push({ ...key, siteId: "" });
      }
    }

    return {
      activeParticipantsCount: activeParticipants,
      compliantRecords: compliantRecords,
      noncompliantRecords: noncompliantRecords,
      hasError: false,
    };
  } catch (error) {
    telemetry.logError(error);
    return {
      activeParticipantsCount: 0,
      compliantRecords: new Array<ParticipantRecord>(),
      noncompliantRecords: new Array<NonCompliantRecord>(),
      hasError: true,
    };
  }
}
